import { Button, Heading, HStack, Stack, Tag, useDisclosure } from '@chakra-ui/react';
import {
  createPrismaQuery,
  createPrismaSort,
  DataTable,
  DataTableAddFilter,
  DataTableFilter,
  DataTableFilters,
  DataTableSearch,
  Loosen,
  useCrud,
} from '@pelicargo/datatable';
import { Show } from '@pelicargo/ui';
import { getPrettyMarkupConfigurationType, markupConfigurationTypeOptions } from '@pelicargo/utils';
import { MarkupConfigurationType } from '@prisma/client';
import { createColumnHelper } from '@tanstack/react-table';
import { useCallback, useMemo, useState } from 'react';

import { MarkupConfigurationDrawer } from '../components/Drawers/MarkupConfigurationDrawer';
import { useAuth } from '../context/AuthProvider';
import { MarkupConfigurationRow, useMarkupConfigurations } from '../hooks/markup-configuration/useMarkupConfigurations';

const baseFilters: DataTableFilter[] = [
  {
    name: 'type',
    label: 'Type',
    type: 'string',
    inputType: 'checkbox',
    operator: 'in',
    value: [],
    options: markupConfigurationTypeOptions,
  },
  {
    name: 'is_restricted',
    label: 'Restricted',
    type: 'boolean',
    inputType: 'radio',
    operator: 'equals',
    value: undefined,
    options: [
      { label: 'Restricted', value: true },
      { label: 'Not Restricted', value: false },
    ],
  },
];

// Only meaningful across configuration types and tenants, so hidden from whitelabel tenant admins
const SUPER_ADMIN_COLUMNS = ['type', 'tenant_id', 'account_as_fulfillment_partner', 'is_restricted'];

export const MarkupConfigurationsScreen = () => {
  const drawer = useDisclosure();
  // Whitelabel tenant admins only manage Tenant markups here (PEL-3045), so the
  // type/tenant/FP controls are noise for them. Peli's own admins see everything.
  const { knownTenants, isSuperAdminOrSystemTenant } = useAuth();

  const [selectedId, setSelectedId] = useState<number | null>(null);

  // Tenant options depend on who's logged in, so this filter can't live at module scope
  const markupConfigurationFilters = useMemo<DataTableFilter[]>(() => {
    if (!isSuperAdminOrSystemTenant) return [];

    return [
      ...baseFilters,
      {
        name: 'tenant_id',
        label: 'Tenant',
        type: 'string',
        inputType: 'checkbox',
        operator: 'in',
        value: [],
        options: knownTenants.map((tenant) => ({ label: tenant, value: tenant })),
      },
    ];
  }, [isSuperAdminOrSystemTenant, knownTenants]);
  const showFilters = markupConfigurationFilters.length > 0;

  const { query, handleQueryChange, filters, setFilters, sorting, setSorting, pagination, setPagination, take, skip } =
    useCrud<typeof markupConfigurationFilters>();

  const { data, count, refetch } = useMarkupConfigurations({
    skip,
    take,
    search: query,
    filters: {
      ...createPrismaQuery(filters),
      ...(isSuperAdminOrSystemTenant ? {} : { type: { in: [MarkupConfigurationType.TENANT] } }),
    },
    orderBy: createPrismaSort(sorting),
  });

  const handleRowClick = useCallback(
    (row: MarkupConfigurationRow) => {
      setSelectedId(row?.id);
      drawer.onOpen();
    },
    [drawer],
  );

  const handleCreate = () => {
    setSelectedId(null);
    drawer.onOpen();
  };

  const handleAddFilter = (filter: Loosen<typeof markupConfigurationFilters>[0]) => () => {
    setFilters((prevFilters) => [...prevFilters, filter]);
  };

  const handleClose = () => {
    setSelectedId(null);
    refetch();
    drawer.onClose();
  };

  const columnHelper = createColumnHelper<MarkupConfigurationRow>();
  const columns = useMemo(() => {
    const allColumns = [
      columnHelper.accessor('id', { header: 'ID', size: 0, minSize: 0 }),
      columnHelper.accessor('label', { header: 'Label' }),
      columnHelper.accessor('type', {
        header: 'Type',
        cell: (cell) => getPrettyMarkupConfigurationType(cell.getValue()),
      }),
      columnHelper.accessor('tenant_id', {
        header: 'Tenant',
        cell: (cell) => (
          <Tag size="sm" variant="subtle">
            {cell.getValue()}
          </Tag>
        ),
      }),
      columnHelper.accessor('account_as_fulfillment_partner', {
        header: 'Fulfillment Partner',
        enableSorting: false,
        cell: (cell) => cell.getValue()?.name ?? '—',
      }),
      columnHelper.accessor('is_restricted', {
        header: 'Restricted',
        cell: (cell) => {
          const isRestricted = cell.getValue();

          return (
            <Show if={isRestricted}>
              <Tag size="sm" colorScheme="red">
                Restricted
              </Tag>
            </Show>
          );
        },
      }),
      columnHelper.display({
        id: 'scope',
        header: 'Scope',
        cell: (cell) => {
          const row = cell.row.original;
          // Empty relation + `is_allowed_all_*` means "matches everything" — show that, not "0"
          const summarise = (label: string, isAll: boolean, total: number) =>
            `${label}: ${isAll || !total ? 'All' : total}`;

          return (
            <HStack spacing="2">
              <Tag size="sm">{summarise('Origins', row.is_allowed_all_origins, row._count.origin_airports)}</Tag>
              <Tag size="sm">{summarise('Airlines', row.is_allowed_all_airlines, row._count.providers)}</Tag>
              <Tag size="sm">
                {summarise('Requestors', row.is_allowed_all_requestors, row._count.account_as_requestors)}
              </Tag>
            </HStack>
          );
        },
      }),
      columnHelper.display({
        id: 'markups',
        header: 'Markups',
        cell: (cell) => cell.row.original._count.markups,
      }),
    ];
    if (isSuperAdminOrSystemTenant) return allColumns;

    return allColumns.filter((column) => {
      const key = 'accessorKey' in column ? column.accessorKey : column.id;
      return !SUPER_ADMIN_COLUMNS.includes(key as string);
    });
  }, [columnHelper, isSuperAdminOrSystemTenant]);

  return (
    <>
      <Stack spacing="0" h="full">
        <Stack p="4" spacing="4">
          <Stack direction="row" justify="space-between" zIndex="99" w="full">
            <Stack direction="row" align="center" w="full">
              <Heading variant="h3">Markup Configurations</Heading>
              <DataTableSearch value={query} onChange={handleQueryChange} />
              <Show if={showFilters}>
                <DataTableAddFilter
                  activeFilters={filters?.map((filter) => filter.name)}
                  options={markupConfigurationFilters}
                  handleAddFilter={handleAddFilter}
                />
              </Show>
            </Stack>
            <Button colorScheme="brand" onClick={handleCreate}>
              Create Markup Configuration
            </Button>
          </Stack>
          <Show if={filters.length > 0}>
            <DataTableFilters filters={filters} setFilters={setFilters} onClear={() => setFilters([])} />
          </Show>
        </Stack>
        <DataTable
          columns={columns}
          data={data ?? []}
          dataCount={count ?? 0}
          pagination={pagination}
          setPagination={setPagination}
          sorting={sorting}
          setSorting={setSorting}
          onRowClick={handleRowClick}
        />
      </Stack>
      <Show if={drawer.isOpen}>
        <MarkupConfigurationDrawer id={selectedId} isOpen={drawer.isOpen} onClose={handleClose} />
      </Show>
    </>
  );
};
